import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { deleteCheapestDestination, getCheapestDestinations, getCities } from '../../../../../api/endpoints/cmsContent';
import type { CheapestDestination, City } from '../../../../../api/types/cmsContent';
import { ApiErrorAlert } from '../../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../../components/ConfirmDialog';
import { ListSearchField } from '../../../../../components/ListSearchField';
import { CheapestDestinationFormDialog } from '../CheapestDestinationFormDialog';

/** List rendered as a plain MUI Table (@mui/x-data-grid is banned under this project's
 *  pinned TypeScript 4.9.5). Confirmed via local-backend swagger + full live curl round-trip
 *  (see api/types/cmsContent.ts header). This page is only ever rendered as a tab inside
 *  `SiteDetailPage` for one specific site, so `siteId` comes in as a fixed prop. */
export default function CheapestDestinationListPage({ siteId }: { siteId: number }) {
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<CheapestDestination | null>(null);
  const [deleting, setDeleting] = useState<CheapestDestination | null>(null);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const query = useQuery({
    queryKey: ['cms', 'content', 'cheapestDestinations', siteId],
    queryFn: () => getCheapestDestinations(siteId),
  });
  const citiesQuery = useQuery({ queryKey: ['cms', 'content', 'cities', siteId], queryFn: () => getCities(siteId) });
  const cities: City[] = useMemo(() => citiesQuery.data?.cities ?? [], [citiesQuery.data]);
  const cityNameById = useMemo(() => new Map(cities.map((c) => [c.id, c.name ?? c.displayName ?? `#${c.id}`])), [cities]);

  const rows: CheapestDestination[] = useMemo(() => query.data?.cheapestDestionations ?? [], [query.data]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('tr');
    if (!term) return rows;
    return rows.filter((row) =>
      [row.fromCode, row.toCode, row.link, row.airlineCode]
        .filter((v) => v !== undefined && v !== null && v !== '')
        .join(' ')
        .toLocaleLowerCase('tr')
        .includes(term)
    );
  }, [rows, search]);

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteCheapestDestination(id),
    onSuccess: () => {
      enqueueSnackbar('Kayıt silindi.', { variant: 'success' });
      queryClient.invalidateQueries({ queryKey: ['cms', 'content', 'cheapestDestinations'] });
      setDeleting(null);
    },
    onError: (err) => enqueueSnackbar(err instanceof Error ? err.message : 'İşlem başarısız.', { variant: 'error' }),
  });

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">En Ucuz Rotalar</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Rota
        </Button>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap', gap: 2 }}>
        <ListSearchField value={search} onChange={setSearch} placeholder="Kalkış, varış, havayolu, link..." />
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kalkış</TableCell>
              <TableCell>Varış</TableCell>
              <TableCell>Tarih</TableCell>
              <TableCell>Fiyat</TableCell>
              <TableCell>Havayolu</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    {rows.length === 0 ? 'Kayıtlı rota bulunamadı.' : 'Aramayla eşleşen rota bulunamadı.'}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {filteredRows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.fromCity_Id ? cityNameById.get(row.fromCity_Id) ?? row.fromCode : row.fromCode ?? '—'}</TableCell>
                <TableCell>{row.toCity_Id ? cityNameById.get(row.toCity_Id) ?? row.toCode : row.toCode ?? '—'}</TableCell>
                <TableCell>{row.startDate ? row.startDate.slice(0, 10) : '—'}</TableCell>
                <TableCell>{row.price !== undefined ? `${row.price} ${row.priceCurrency ?? ''}`.trim() : '—'}</TableCell>
                <TableCell>{row.airlineCode ?? '—'}</TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)} aria-label="Düzenle">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => setDeleting(row)} aria-label="Sil">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <CheapestDestinationFormDialog
        open={createOpen}
        item={null}
        cities={cities}
        siteId={siteId}
        onClose={() => setCreateOpen(false)}
      />
      <CheapestDestinationFormDialog
        open={!!editing}
        item={editing}
        cities={cities}
        siteId={siteId}
        onClose={() => setEditing(null)}
      />

      <ConfirmDialog
        open={!!deleting}
        title="Rotayı sil"
        description={`"${deleting?.fromCode ?? ''} → ${deleting?.toCode ?? ''}" rotasını silmek istediğinize emin misiniz?`}
        loading={deleteMutation.isPending}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
        onCancel={() => setDeleting(null)}
      />
    </Box>
  );
}
